import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileText, Upload, Trash2, Smartphone, Download, Filter } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, API, getToken, formatApiError } from "@/lib/api";

const CATEGORIES = [
  { value: "consent",            label: "Consent" },
  { value: "advance_directive",  label: "Advance Directive (DNR)" },
  { value: "insurance",          label: "Insurance" },
  { value: "referral_letter",    label: "Referral Letter" },
  { value: "lab_result",         label: "Lab Result" },
  { value: "imaging",            label: "Imaging" },
  { value: "prescription",       label: "Prescription" },
  { value: "discharge_summary",  label: "Discharge Summary" },
  { value: "legal_id",           label: "Legal / ID" },
  { value: "other",              label: "Other" },
];

const SOURCE_TONE = {
  Direct:     "bg-teal-50 text-teal-700 border-teal-200",
  Mobile:     "bg-sky-50 text-sky-700 border-sky-200",
  Onboarding: "bg-blue-50 text-blue-700 border-blue-200",
  Hotline:    "bg-amber-50 text-amber-700 border-amber-200",
  "Care Plan":"bg-indigo-50 text-indigo-700 border-indigo-200",
  "Home Visit":"bg-emerald-50 text-emerald-700 border-emerald-200",
  Support:    "bg-slate-50 text-slate-700 border-slate-200",
  Teleconsult:"bg-purple-50 text-purple-700 border-purple-200",
};

function fmtSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function UploadDialog({ open, onOpenChange, patientId, onDone, defaultMobileSync }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("other");
  const [note, setNote] = useState("");
  const [mobileSync, setMobileSync] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setFile(null); setCategory("other"); setNote(""); setMobileSync(!!defaultMobileSync); } }, [open, defaultMobileSync]);

  async function submit() {
    if (!file) { toast.error("Please select a file."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      if (note.trim()) fd.append("note", note.trim());
      fd.append("mobile_sync", String(mobileSync));
      await api.post(`/patients/${patientId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      onDone && onDone(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="doc-upload-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Upload patient document</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1.5">File</div>
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
              accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp3,.mp4,.wav,.webm"
              className="text-sm w-full" data-testid="doc-upload-file" />
            {file && <div className="text-[11px] text-[#9CA3AF] mt-1">{file.name} · {fmtSize(file.size)}</div>}
            <div className="text-[11px] text-[#9CA3AF] mt-1">PDF/DOC/IMG/AUDIO/VIDEO up to 20 MB.</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1.5">Category</div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="doc-upload-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1.5">Note (optional)</div>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Short description shown alongside the document"
              data-testid="doc-upload-note" />
          </div>
          <div className="flex items-center justify-between border-t border-[#E5E7EB] pt-3">
            <div>
              <div className="text-sm font-medium text-[#1F2937]">Sync to mobile app</div>
              <div className="text-[11px] text-[#9CA3AF]">Patient sees this on their mobile app.</div>
            </div>
            <Switch checked={mobileSync} onCheckedChange={setMobileSync} data-testid="doc-upload-mobile-sync" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="doc-upload-submit">
            <Upload className="w-4 h-4 mr-1.5" /> {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PatientDocumentsTab({ patientId, canUpload, isAdmin }) {
  const [data, setData] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [savingMode, setSavingMode] = useState(false);
  const [hasUploadPerm, setHasUploadPerm] = useState(isAdmin);

  useEffect(() => {
    if (isAdmin) { setHasUploadPerm(true); return; }
    api.get("/auth/permissions")
      .then((r) => {
        const perms = r.data.permissions || [];
        setHasUploadPerm(perms.includes("*") || perms.includes("documents.upload"));
      })
      .catch(() => setHasUploadPerm(false));
  }, [isAdmin]);

  const showUpload = canUpload && hasUploadPerm;

  const load = useCallback(() => {
    api.get(`/patients/${patientId}/documents`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiError(e)); setData({ items: [], count: 0, mobile_document_sync_mode: "all" }); });
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const mode = data?.mobile_document_sync_mode || "all";

  const sources = useMemo(() => {
    const map = new Map();
    items.forEach((it) => map.set(it.source_label, (map.get(it.source_label) || 0) + 1));
    return Array.from(map.entries()).sort();
  }, [items]);

  const filtered = useMemo(() => items.filter((it) =>
    (sourceFilter === "all" || it.source_label === sourceFilter) &&
    (categoryFilter === "all" || it.category === categoryFilter)
  ), [items, sourceFilter, categoryFilter]);

  async function setSyncMode(newMode) {
    setSavingMode(true);
    try {
      await api.put(`/patients/${patientId}/documents/_settings`, { mode: newMode });
      toast.success(newMode === "all" ? "All patient documents will sync to mobile" : "Only toggled documents will sync");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingMode(false); }
  }

  async function toggleDocSync(doc, next) {
    try {
      await api.patch(`/patients/${patientId}/documents/${doc.id}`, { mobile_sync: next });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function onDelete(doc) {
    if (doc.source_label !== "Direct") {
      toast.error("Only documents uploaded directly from this tab can be deleted here. Remove workflow documents from their source.");
      return;
    }
    if (!window.confirm(`Delete "${doc.filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/patients/${patientId}/documents/${doc.id}`);
      toast.success("Document deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function downloadHref(doc) {
    const token = getToken();
    return `${API}/uploads/${doc.id}/download?auth=${encodeURIComponent(token || "")}`;
  }

  return (
    <div data-testid="patient-documents-tab">
      {/* Mobile sync toggle bar */}
      <div className="hah-card p-4 mb-4 flex items-start justify-between gap-4" data-testid="doc-sync-mode-bar">
        <div>
          <div className="text-sm font-semibold text-[#1F2937] flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-[#0B7E8C]" /> Mobile-app document sync
          </div>
          <div className="text-xs text-[#6B7280] mt-1">
            The <b>Mobile</b> toggle on each row controls what the patient sees on their mobile app.
            The setting below only decides the <b>default</b> for new uploads.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSyncMode("all")}
            disabled={savingMode || mode === "all"}
            className={`px-3 py-1 rounded-full border text-[12px] font-medium ${mode === "all" ? "border-[#0B7E8C] bg-[#0B7E8C]/10 text-[#0B7E8C]" : "border-[#E5E7EB] text-[#6B7280] hover:border-[#0B7E8C]/50"}`}
            data-testid="doc-sync-mode-all"
          >Default: sync new uploads</button>
          <button
            onClick={() => setSyncMode("selected")}
            disabled={savingMode || mode === "selected"}
            className={`px-3 py-1 rounded-full border text-[12px] font-medium ${mode === "selected" ? "border-[#0B7E8C] bg-[#0B7E8C]/10 text-[#0B7E8C]" : "border-[#E5E7EB] text-[#6B7280] hover:border-[#0B7E8C]/50"}`}
            data-testid="doc-sync-mode-selected"
          >Default: don't sync</button>
        </div>
      </div>

      {/* Filter + upload */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-[#6B7280]" />
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
            className="text-sm px-2 py-1 rounded border border-[#E5E7EB] bg-white"
            data-testid="doc-filter-source">
            <option value="all">All sources ({items.length})</option>
            {sources.map(([lbl, ct]) => <option key={lbl} value={lbl}>{lbl} ({ct})</option>)}
          </select>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-sm px-2 py-1 rounded border border-[#E5E7EB] bg-white"
            data-testid="doc-filter-category">
            <option value="all">All categories</option>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        {showUpload && (
          <Button onClick={() => setUploadOpen(true)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
            data-testid="doc-upload-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Upload document
          </Button>
        )}
      </div>

      {/* List */}
      {data === null ? (
        <div className="text-sm text-[#6B7280]" data-testid="documents-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="hah-card p-8 text-center" data-testid="documents-empty">
          <FileText className="w-10 h-10 mx-auto text-[#D1D5DB] mb-2" />
          <div className="text-sm text-[#6B7280]">No documents yet.</div>
          <div className="text-[11px] text-[#9CA3AF] mt-1">
            Documents uploaded here, from the mobile app, or from any workflow (onboarding, hotline, visits, care plan, tickets) will show up.
          </div>
        </div>
      ) : (
        <div className="hah-card overflow-hidden" data-testid="documents-list">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="px-3 py-2">Document</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Uploaded</th>
                <th className="px-3 py-2 text-center">Mobile</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {filtered.map((d) => (
                <tr key={d.id} data-testid={`doc-row-${d.id}`} className="hover:bg-slate-50/60">
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-[#1F2937] truncate max-w-[280px]">{d.filename}</div>
                    <div className="text-[11px] text-[#9CA3AF]">{fmtSize(d.size_bytes)} · {d.content_type}</div>
                    {d.note && <div className="text-[11px] text-[#6B7280] italic mt-0.5 line-clamp-1">{d.note}</div>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${SOURCE_TONE[d.source_label] || SOURCE_TONE.Patient}`}>
                      {d.source_label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-[#374151] capitalize">
                    {(CATEGORIES.find((c) => c.value === d.category) || {}).label || (d.category || "—")}
                  </td>
                  <td className="px-3 py-2.5 text-[#6B7280]">
                    <div>{fmtDate(d.uploaded_at)}</div>
                    <div className="text-[11px] text-[#9CA3AF]">{d.uploaded_by_name}</div>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <Switch checked={!!d.mobile_sync} onCheckedChange={(v) => toggleDocSync(d, v)}
                      data-testid={`doc-mobile-toggle-${d.id}`} />
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      <a href={downloadHref(d)} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#E5E7EB] text-[12px] text-[#374151] hover:bg-slate-50"
                        data-testid={`doc-download-${d.id}`}>
                        <Download className="w-3.5 h-3.5" /> Download
                      </a>
                      {isAdmin && d.source_label === "Direct" && (
                        <button onClick={() => onDelete(d)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-[12px] text-red-600 hover:bg-red-50"
                          data-testid={`doc-delete-${d.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen}
        patientId={patientId} onDone={load} defaultMobileSync={mode !== "selected"} />
    </div>
  );
}
